/**
 * @fileoverview Connect matter-scoped Clockify billing to summary and per-attorney presentation. Backend calculation owns rate/rounding semantics; `BillingTable.js` presents the accepted totals. Explicit resync has different provider/cache effects from reading a report. Billing evidence here is distinct from invoices, payment journals, and settlement receipts.
 */

import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import ToastContext from '../../ToastContext';
import BillingTable from './BillingTable';
import { AppSpinner, ChartCanvas, AppButton } from '../shared';
import './Billing.css';
import AppIcon from "../shared/AppIcon";

const CHART_PALETTE = [
{ fill: 'rgba(59, 130, 246, 0.72)', border: 'rgb(59, 130, 246)' },
{ fill: 'rgba(20, 184, 166, 0.72)', border: 'rgb(20, 184, 166)' },
{ fill: 'rgba(249, 115, 22, 0.72)', border: 'rgb(249, 115, 22)' },
{ fill: 'rgba(168, 85, 247, 0.72)', border: 'rgb(168, 85, 247)' },
{ fill: 'rgba(236, 72, 153, 0.72)', border: 'rgb(236, 72, 153)' },
{ fill: 'rgba(99, 102, 241, 0.72)', border: 'rgb(99, 102, 241)' }];




function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value) || 0);
}



function formatHours(value) {
  return `${(Number(value) || 0).toFixed(2)} hrs`;
}



function readChartTheme() {
  if (typeof window === 'undefined') {
    return {
      textColor: '#111827',
      secondaryTextColor: '#6b7280',
      borderColor: '#e5e7eb',
      surfaceColor: '#ffffff'
    };
  }

  const styles = window.getComputedStyle(document.documentElement);
  const readVar =

  (name, fallback) => styles.getPropertyValue(name).trim() || fallback;

  return {
    textColor: readVar('--text-color', '#111827'),
    secondaryTextColor: readVar('--text-color-secondary', '#6b7280'),
    borderColor: readVar('--surface-border', '#e5e7eb'),
    surfaceColor: readVar('--surface-card', '#ffffff')
  };
}



function useChartTheme() {
  const [theme, setTheme] = useState(() => readChartTheme());

  useEffect(

  () => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateTheme =

    () => setTheme(readChartTheme());
    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateTheme);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(updateTheme);
    }

    return () => {
      observer.disconnect();
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateTheme);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(updateTheme);
      }
    };
  }, []);

  return theme;
}



function buildBarOptions({ chartTheme, formatTick }) {
  return {
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: chartTheme.surfaceColor,
        titleColor: chartTheme.textColor,
        bodyColor: chartTheme.textColor,
        borderColor: chartTheme.borderColor,
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          color: chartTheme.secondaryTextColor
        }
      },
      y: {
        beginAtZero: true,
        border: {
          display: false
        },
        grid: {
          color: chartTheme.borderColor,
          drawTicks: false
        },
        ticks: {
          color: chartTheme.secondaryTextColor,
          callback: formatTick
        }
      }
    }
  };
}



function Billing() {
  const { MatterNumber } = useParams();
  const showToast = useContext(ToastContext);
  const chartTheme = useChartTheme();

  const [loading, setLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [billingData, setBillingData] = useState(null);
  const [billingError, setBillingError] = useState(null);

  const fetchBilling = useCallback(

  () => {
    setLoading(true);
    setBillingData(null);
    setBillingError(null);
    api.get(`/clockify_billing_data/${MatterNumber}`)
    .then((response) => {
      setBillingData(response.data);
    })
    .catch((error) => {
      setBillingError(error.response?.data?.error || 'Billing could not be loaded. Please retry.');
    })
    .finally(() => {
      setLoading(false);
    });
  }, [MatterNumber]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleResync = useCallback(

  () => {
    setResyncing(true);
    setBillingError(null);
    api.get(`/resync_clockify_billing_data/${MatterNumber}`)
    .then((response) => {
      setBillingData(response.data);
      showToast({ severity: 'success', summary: 'Successful', detail: 'Billing data is up to date' });
    })
    .catch((error) => {
      setBillingError(error.response?.data?.error || 'Billing could not be refreshed. Please retry.');
      showToast({ severity: 'error', summary: 'Error', detail: error.message });
    })
    .finally(() => {
      setResyncing(false);
    });
  }, [MatterNumber, showToast]);

  const attorneyRows = useMemo(() => {
    const totals = billingData?.clockify_user_totals || {};

    return Object.keys(totals)
    .map(

    (name) => {
      const item = totals[name] || {};
      const rateValue = Number(item.rate) || 0;
      const hoursValue = Number(item.total_duration_hours) || 0;
      const billValue = Number(item.total_bill) || 0;

      return {
        name,
        rateValue,
        rateDisplay: formatCurrency(rateValue),
        hoursValue,
        hoursDisplay: hoursValue.toFixed(2),
        billValue,
        billDisplay: formatCurrency(billValue)
      };
    })
    .sort((a, b) => b.billValue - a.billValue);
  }, [billingData]);

  const totalHours = Number(billingData?.grand_total_hours) || 0;
  const totalBill = Number(billingData?.grand_total_bill) || 0;
  const attorneyCount = attorneyRows.length;

  const hoursChartData = useMemo(() => {
    return {
      labels: attorneyRows.map((row) => row.name),
      datasets: [
      {
        label: 'Hours Worked',
        data: attorneyRows.map((row) => Number(row.hoursDisplay)),
        backgroundColor: attorneyRows.map((_, index) => CHART_PALETTE[index % CHART_PALETTE.length].fill),
        borderColor: attorneyRows.map((_, index) => CHART_PALETTE[index % CHART_PALETTE.length].border),
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 46
      }]

    };
  }, [attorneyRows]);

  const billChartData = useMemo(() => {
    return {
      labels: attorneyRows.map((row) => row.name),
      datasets: [
      {
        label: 'Amount Billed',
        data: attorneyRows.map((row) => row.billValue),
        backgroundColor: attorneyRows.map((_, index) => CHART_PALETTE[index % CHART_PALETTE.length].fill),
        borderColor: attorneyRows.map((_, index) => CHART_PALETTE[index % CHART_PALETTE.length].border),
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 46
      }]

    };
  }, [attorneyRows]);

  const hoursChartOptions = useMemo(() => {
    return buildBarOptions({
      chartTheme,
      formatTick: (value) => `${value}`
    });
  }, [chartTheme]);

  const billChartOptions = useMemo(() => {
    return buildBarOptions({
      chartTheme,
      formatTick: (value) => `$${value}`
    });
  }, [chartTheme]);

  if (loading) {
    return (
      <div className="case-billing case-billing--loading">
                <AppSpinner />
            </div>);

  }

  if (billingError || !billingData) {
    return (
      <div className="case-billing">
                <section className="billing-empty-state" role={billingError ? 'alert' : undefined}>
                    <div className="billing-empty-icon">
                        <AppIcon icon={billingError ? 'pi pi-exclamation-triangle' : 'pi pi-inbox'} aria-hidden="true" />
                    </div>
                    <div className="billing-empty-title">{billingError ? 'Billing unavailable' : 'No Clockify data found'}</div>
                    <div className="billing-empty-copy">
                        {billingError || 'Billing totals have not been loaded for this matter yet.'}
                    </div>
                    <AppButton
            icon="pi pi-refresh"
            variant="secondary"
            label={billingError ? 'Retry' : 'Try Resync'}
            onClick={billingError ? fetchBilling : handleResync}
            disabled={resyncing} />

                </section>
            </div>);

  }

  return (
    <div className="case-billing">
            <section className="billing-hero">
                <div className="billing-hero-copy">
                    <div className="billing-eyebrow">Clockify</div>
                    <h2 className="billing-hero-title">Billing overview</h2>
                    <p className="billing-hero-description">
                        Matter-level hours, billed totals, and attorney breakdown for this case.
                    </p>
                </div>

                <div className="billing-hero-actions">
                    {resyncing ?
          <AppButton
            icon="pi pi-spin pi-refresh"
            variant="secondary"
            label="Resyncing..."
            className="billing-resync-button"
            disabled /> :


          <AppButton
            icon="pi pi-refresh"
            variant="secondary"
            label="Resync Clockify"
            className="billing-resync-button"
            onClick={handleResync} />}


                </div>
            </section>

            {attorneyRows.length === 0 ?
      <section className="billing-empty-state">
                    <div className="billing-empty-icon">
                        <AppIcon icon="pi pi-clock" aria-hidden="true" />
                    </div>
                    <div className="billing-empty-title">No attorney billing breakdown</div>
                    <div className="billing-empty-copy">
                        Clockify did not return any per-attorney totals for this matter.
                    </div>
                </section> :

      <>
                    <div className="billing-panel-grid">
                        <section className="billing-panel">
                            <div className="billing-panel-header">
                                <div>
                                    <h3 className="billing-panel-title">Hours by Attorney</h3>
                                    <p className="billing-panel-description">
                                        Clockify hours rounded up to six-minute increments for this matter.
                                    </p>
                                </div>
                                <div className="billing-panel-metric">{formatHours(totalHours)}</div>
                            </div>
                            <div className="billing-chart-body">
                                <ChartCanvas type="bar" data={hoursChartData} options={hoursChartOptions} />
                            </div>
                        </section>

                        <section className="billing-panel">
                            <div className="billing-panel-header">
                                <div>
                                    <h3 className="billing-panel-title">Bill by Attorney</h3>
                                    <p className="billing-panel-description">
                                        Total billed amount derived from attorney rate and tracked hours.
                                    </p>
                                </div>
                                <div className="billing-panel-metric">{formatCurrency(totalBill)}</div>
                            </div>
                            <div className="billing-chart-body">
                                <ChartCanvas type="bar" data={billChartData} options={billChartOptions} />
                            </div>
                        </section>
                    </div>

                    <BillingTable rows={attorneyRows} attorneyCount={attorneyCount} />
                </>}


            <section className="billing-rounding-note" aria-label="Billing rounding behavior">
                <div className="billing-rounding-note-title">Rounding</div>
                <p className="billing-rounding-note-copy">
                    Billing totals use six-minute buckets based on whole minutes of tracked time.
                    0:01-6:59 rounds to 6 minutes, 7:00-12:59 rounds to 12 minutes, and
                    13:00-18:59 rounds to 18 minutes.
                </p>
            </section>
        </div>);

}

export default Billing;
